import React, { useMemo, ChangeEvent } from 'react';
import { PhoneIncoming, PhoneOutgoing, Headset, ClipboardCopy } from 'lucide-react';
import { Checkbox, Button, Chip, Table, TableColumn, TableHeaderCell } from 'common/components';
import {
    formatDisposition,
    formatLeadStatus,
    formatCallOutcome,
    getOutcomeChipColor,
    getLeadStatusChipColor,
} from '../../utils';
import type { Lead } from '../CallDetailsModal/CallDetailsModal';
import type { SortField, SortDirection } from 'types/call.type';
import { CallTypeCategories } from 'types/call.type';
import { DateCell, StatusCell, DispositionCell, DurationSortHeader, PartyStatusCell } from './components';
import { DateRangeType } from '../../AgenticWorkflowDetail.types';
import { useGetCallDispositions } from 'services/hooks/queries/useGetCallDispositions';

import styles from './CallsTable.module.scss';

export type TableFilterOption = { value: string; label: string; category?: string };

export type CallsTableFilterName = 'leadStatus' | 'callStatus' | 'callDirection' | 'disposition';

export interface CallsTableDateRangeConfig {
    type: DateRangeType;
    onTypeChange: (range: string) => void;
    setDateRange: (dateRange: [Date | null, Date | null]) => void;
}

export interface CallsTableProps {
    leads: Lead[];
    selectedCalls: string[];
    selectedCall: Lead | null;
    sortField: SortField;
    sortDirection: SortDirection;
    searchTerm: string;
    filterLeadStatus: string;
    filterCallStatus: string;
    filterCallDirection: string;
    filterDisposition: string;
    partyStages?: string[];
    onSortChange: (field: SortField, newSortDirection: SortDirection) => void;
    onFilterChange: (filter: CallsTableFilterName, value: string) => void;
    onSelectAllCheckboxChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onCheckboxChange: (callId: string) => void;
    onCallDetails: (lead: Lead) => void;
    dateRangeConfig: CallsTableDateRangeConfig;
    isLoading: boolean;
    showColumnFilters?: boolean;
}

export const CallsTable = ({
    isLoading,
    leads,
    selectedCalls,
    selectedCall,
    sortField,
    sortDirection,
    searchTerm,
    filterLeadStatus,
    filterCallStatus,
    filterCallDirection,
    filterDisposition,
    partyStages = [],
    onSortChange,
    onFilterChange,
    onSelectAllCheckboxChange,
    onCheckboxChange,
    onCallDetails,
    dateRangeConfig,
    showColumnFilters = true,
}: CallsTableProps) => {
    const { callDispositions } = useGetCallDispositions();
    const getDirectionLabel = (callDirection?: string) => {
        const value = callDirection?.toUpperCase();
        if (value?.includes('INBOUND')) return 'Inbound';
        if (value?.includes('OUTBOUND')) return 'Outbound';
        if (value?.includes('MANUAL')) return 'Manual';
        return '-';
    };

    const partyStageOptions = useMemo<TableFilterOption[]>(
        () => partyStages.map((stage) => ({ value: stage, label: stage })),
        [partyStages]
    );

    const statusOptions = useMemo<TableFilterOption[]>(() => {
        return Object.keys(CallTypeCategories).map((outcome) => ({
            label: formatCallOutcome(outcome),
            value: outcome,
            category: CallTypeCategories[outcome as keyof typeof CallTypeCategories],
        }));
    }, []);

    const dispositionOptions = useMemo<TableFilterOption[] | undefined>(
        () =>
            callDispositions?.dispositions.map((d) => ({
                label: formatDisposition(d),
                value: d,
            })),
        [callDispositions]
    );

    const handleCopy = async (e: React.MouseEvent, lead: Lead) => {
        e.stopPropagation();
        try {
            const text = `Call ID: ${lead.callId}\nCall Date: ${lead.callDate}\nCall Time: ${lead.callTime}\nCall Status: ${lead.outcome}\nCall Party Status: ${lead.crmStatus}\nCall Direction: ${getDirectionLabel(lead.callDirection)}\nCall Duration: ${lead.duration}\nCall CRM ID: ${lead.crmId}\nCall Name: ${lead.name}\nCall Number: ${lead.number}\nCall Transfer To: ${lead.transferTo}\nCall Disposition: ${lead.disposition}`;
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const hasActiveFilters =
        searchTerm.trim() || filterLeadStatus || filterCallStatus || filterCallDirection || filterDisposition;
    const noDataMessage = hasActiveFilters ? 'No matching calls found.' : 'No calls data available for this workflow.';

    const columns: TableColumn<Lead>[] = useMemo(
        () => [
            {
                key: 'actions',
                title: (
                    <Checkbox
                        checked={!!leads.length && leads.every((lead) => selectedCalls.includes(lead.callId))}
                        indeterminate={
                            !!leads.length &&
                            leads.some((lead) => selectedCalls.includes(lead.callId)) &&
                            !leads.every((lead) => selectedCalls.includes(lead.callId))
                        }
                        onChange={onSelectAllCheckboxChange}
                    />
                ),
                width: 40,
                align: 'left',
                render: (_, record) => (
                    <div onClick={(e) => e.stopPropagation()} className={styles.actionsCellContent}>
                        <div className={styles.copyButton}>
                            <Button icon={ClipboardCopy} iconOnly size='sm' onClick={(e) => handleCopy(e, record)} />
                        </div>
                        <Checkbox
                            checked={selectedCalls.includes(record.callId)}
                            onChange={() => onCheckboxChange(record.callId)}
                        />
                    </div>
                ),
            },
            {
                key: 'callDate',
                title: showColumnFilters ? (
                    <DateCell
                        dateRangeType={dateRangeConfig.type}
                        onSortChange={onSortChange}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        handleDateRangeChange={dateRangeConfig.onTypeChange}
                        setDateRange={dateRangeConfig.setDateRange}
                    />
                ) : (
                    <TableHeaderCell
                        label='Date'
                        sort={{
                            isSorted: sortField === 'callDate',
                            direction: sortDirection,
                            onSort: () => onSortChange('callDate', sortDirection === 'asc' ? 'desc' : 'asc'),
                        }}
                    />
                ),
                width: 140,
                className: styles.dateCell,
                render: (_, record) => (
                    <>
                        <div>{record.callDate}</div>
                        <div className={styles.timeText}>
                            {getDirectionLabel(record.callDirection) === 'Inbound' && (
                                <PhoneIncoming
                                    size={14}
                                    className={`${styles.callDirectionIcon} ${styles.callInbound}`}
                                    fill='currentColor'
                                />
                            )}
                            {getDirectionLabel(record.callDirection) === 'Outbound' && (
                                <PhoneOutgoing
                                    size={14}
                                    className={`${styles.callDirectionIcon} ${styles.callOutbound}`}
                                    fill='currentColor'
                                />
                            )}
                            {getDirectionLabel(record.callDirection) === 'Manual' && (
                                <Headset size={14} className={styles.callDirectionIcon} color='var(--primary-purple)' />
                            )}
                            {record.callTime}
                        </div>
                    </>
                ),
            },
            {
                key: 'outcome',
                title: showColumnFilters ? (
                    <StatusCell
                        filterStatus={filterCallStatus}
                        statusOptions={statusOptions}
                        onFilterStatus={(value) => onFilterChange('callStatus', value)}
                    />
                ) : (
                    'Status'
                ),
                width: 140,
                render: (_, record) => (
                    <Chip
                        style={{ maxWidth: '100%' }}
                        variant='filled'
                        corner='pill'
                        color={getOutcomeChipColor(record.outcome)}
                        size='sm'
                    >
                        {formatCallOutcome(record.outcome)}
                    </Chip>
                ),
            },
            {
                key: 'duration',
                title: (
                    <DurationSortHeader
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSortChange={onSortChange}
                    />
                ),
                width: 100,
                render: (_, record) => <span title={record.durationTooltip || ''}>{record.duration}</span>,
            },
            {
                key: 'partyStatus',
                title: showColumnFilters ? (
                    <PartyStatusCell
                        filterLeadStatus={filterLeadStatus}
                        partyStageOptions={partyStageOptions}
                        onFilterLeadStatus={(value) => onFilterChange('leadStatus', value)}
                    />
                ) : (
                    'Party status'
                ),
                width: 140,
                render: (_, record) => (
                    <Chip
                        style={{ maxWidth: '100%' }}
                        variant='filled'
                        corner='pill'
                        color={getLeadStatusChipColor(record.crmStatus)}
                        size='sm'
                    >
                        {formatLeadStatus(record.crmStatus)}
                    </Chip>
                ),
            },
            {
                key: 'crmId',
                title: 'CRM ID',
                dataIndex: 'crmId',
                width: 100,
            },
            {
                key: 'name',
                title: 'Name',
                dataIndex: 'name',
                width: 120,
            },
            {
                key: 'number',
                title: 'Number',
                dataIndex: 'number',
                width: 150,
            },
            {
                key: 'transferTo',
                title: 'Transfer to',
                dataIndex: 'transferTo',
                width: 100,
            },
            {
                key: 'disposition',
                width: 120,
                title: showColumnFilters ? (
                    <DispositionCell
                        filterDisposition={filterDisposition}
                        dispositionOptions={dispositionOptions || []}
                        onFilterDisposition={(value) => onFilterChange('disposition', value)}
                    />
                ) : (
                    'Disposition'
                ),
                render: (_, record) =>
                    record.disposition ? (
                        <span>{formatDisposition(record.disposition)}</span>
                    ) : (
                        <span className={styles.emptyCell}>-</span>
                    ),
            },
        ],
        [
            leads,
            selectedCalls,
            sortField,
            sortDirection,
            dateRangeConfig,
            filterCallDirection,
            filterCallStatus,
            filterDisposition,
            filterLeadStatus,
            partyStageOptions,
            statusOptions,
            dispositionOptions,
            onSortChange,
            onFilterChange,
            onSelectAllCheckboxChange,
            onCheckboxChange,
            showColumnFilters,
        ]
    );

    return (
        <div className={styles.leadsTableHover}>
            <Table<Lead>
                data={leads}
                columns={columns}
                emptyText={noDataMessage}
                className={styles.leadsTableContainer}
                rowKey={(record) => record.callId || record.id}
                onRowClick={onCallDetails}
                rowClassName={(record) =>
                    selectedCall?.callId === record.callId
                        ? `${styles.copyCallIdRow} ${styles.selectedRow}`
                        : styles.copyCallIdRow
                }
                showHeader
                variant='secondary'
                hoverable
                size='medium'
                isLoading={isLoading}
            />
        </div>
    );
};
